import { terraContract } from '@terra/shared-authorization';
import { z } from 'zod';
import {
  ApproveMisconfigResearchBodySchema,
  ApproveMisconfigResearchParamsSchema,
  ApproveMisconfigResearchResponseSchema,
  DeleteMisconfigResearchFindingParamsSchema,
  DeleteMisconfigResearchFindingResponseSchema,
  DeleteMisconfigResearchRunParamsSchema,
  DeleteMisconfigResearchRunResponseSchema,
  GetMisconfigResearchRunParamsSchema,
  GetMisconfigResearchRunResponseSchema,
  ListMisconfigResearchRunsQuerySchema,
  ListMisconfigResearchRunsResponseSchema,
  TriggerMisconfigResearchBodySchema,
  TriggerMisconfigResearchResponseSchema,
  ListMisconfigTechnologyAssessmentsQuerySchema,
  ListMisconfigTechnologyAssessmentsResponseSchema,
  BackfillMisconfigTechnologyAssessmentsResponseSchema,
  ReassessMisconfigTechnologyBodySchema,
  ReassessMisconfigTechnologyResponseSchema,
} from './misconfig-research.schemas';

export const cockpitMisconfigResearchRoutes = {
  triggerMisconfigResearch: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'POST',
      path: '/misconfig-research/trigger',
      summary: 'Trigger misconfiguration research for a technology and version',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TriggerMisconfigResearchBodySchema }))
    .output(TriggerMisconfigResearchResponseSchema),
  listMisconfigResearchRuns: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/misconfig-research/runs',
      summary: 'List misconfiguration research runs',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListMisconfigResearchRunsQuerySchema }))
    .output(ListMisconfigResearchRunsResponseSchema),
  getMisconfigResearchRun: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/misconfig-research/runs/{runId}',
      summary: 'Get a misconfiguration research run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: GetMisconfigResearchRunParamsSchema }))
    .output(GetMisconfigResearchRunResponseSchema),
  approveMisconfigResearchRun: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'POST',
      path: '/misconfig-research/runs/{runId}/approve',
      summary:
        'Create signals from a completed or approved misconfiguration research run',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ApproveMisconfigResearchParamsSchema,
        body: ApproveMisconfigResearchBodySchema.optional(),
      }),
    )
    .output(ApproveMisconfigResearchResponseSchema),
  deleteMisconfigResearchFinding: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'DELETE',
      path: '/misconfig-research/runs/{runId}/findings/{findingId}',
      summary: 'Delete a finding from a misconfiguration research run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: DeleteMisconfigResearchFindingParamsSchema }))
    .output(DeleteMisconfigResearchFindingResponseSchema),
  listMisconfigTechnologyAssessments: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'GET',
      path: '/misconfig-research/assessments',
      summary: 'List stored technology misconfiguration assessments',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListMisconfigTechnologyAssessmentsQuerySchema }))
    .output(ListMisconfigTechnologyAssessmentsResponseSchema),
  backfillMisconfigTechnologyAssessments: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'POST',
      path: '/misconfig-research/assessments/backfill',
      summary: 'Assess technologies that do not yet have a stored assessment',
      inputStructure: 'detailed',
    })
    .input(z.object({}))
    .output(BackfillMisconfigTechnologyAssessmentsResponseSchema),
  reassessMisconfigTechnology: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'POST',
      path: '/misconfig-research/assessments/reassess',
      summary: 'Recompute the assessment for one technology',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: ReassessMisconfigTechnologyBodySchema }))
    .output(ReassessMisconfigTechnologyResponseSchema),
  deleteMisconfigResearchRun: terraContract
    .meta({ permissions: ['Cockpit Viewer'] })
    .route({
      method: 'DELETE',
      path: '/misconfig-research/runs/{runId}',
      summary: 'Delete a misconfiguration research run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: DeleteMisconfigResearchRunParamsSchema }))
    .output(DeleteMisconfigResearchRunResponseSchema),
} as const;
